"use client";

import { useState } from "react";
import { createPortal } from "react-dom";
import { useRouter } from "next/navigation";
import { AnimatePresence, motion } from "framer-motion";
import {
  ChevronLeft,
  Grid2x2,
  SlidersHorizontal,
  Sofa,
  X,
} from "lucide-react";

type FilterItem = {
  id: number;
  name: string;
};

type MobileProductsFiltersProps = {
  visibleCount: number;
  allProductsCount: number;
  searchQuery: string;
  selectedCategoryId: number | null;
  selectedCategoryName: string | null;
  selectedProductsCategoryName: string | null;
  selectedNestedChildId: number | null;
  childCategories: FilterItem[];
  nestedChildCategories: FilterItem[];
};

export default function MobileProductsFilters({
  visibleCount,
  allProductsCount,
  searchQuery,
  selectedCategoryId,
  selectedCategoryName,
  selectedProductsCategoryName,
  selectedNestedChildId,
  childCategories,
  nestedChildCategories,
}: MobileProductsFiltersProps) {
  const router = useRouter();
  const [isOpen, setIsOpen] = useState(false);
  const [activePanel, setActivePanel] = useState<"browse" | "filter">("browse");
  const baseFilterOptionClass =
    "w-full rounded px-4 py-2 text-center text-xs font-semibold transition focus:outline-none focus-visible:outline-none active:scale-[0.98]";
  const baseNestedFilterOptionClass =
    "w-full rounded-full px-3 py-2 text-center text-xs font-semibold transition focus:outline-none focus-visible:outline-none active:scale-[0.98]";
  const inactiveFilterOptionClass =
    "border border-[var(--color-line)] bg-[var(--color-surface)] text-[var(--color-ink)] active:border-[var(--color-line-strong)] active:bg-[var(--color-surface)] active:text-[var(--color-ink)]";
  const activeFilterOptionClass =
    "border border-transparent bg-[#ee376a] text-white active:border-transparent active:bg-[#ee376a] active:text-white";
  const activeFiltersCount =
    (selectedCategoryId ? 1 : 0) + (selectedNestedChildId ? 1 : 0);
  const hasNestedFilters =
    Boolean(selectedCategoryId) && nestedChildCategories.length > 0;
  const portalTarget = typeof document !== "undefined" ? document.body : null;
  const encodedQuery = searchQuery.trim()
    ? `&q=${encodeURIComponent(searchQuery.trim())}`
    : "";
  const queryPath = searchQuery.trim()
    ? `/products?q=${encodeURIComponent(searchQuery.trim())}`
    : "/products";

  const openPanel = (panel: "browse" | "filter") => {
    setActivePanel(panel);
    setIsOpen(true);
  };

  const navigateTo = (href: string) => {
    setIsOpen(false);
    router.push(href, { scroll: false });
  };

  return (
    <div className="mt-3 md:hidden">
      <div className="fixed inset-x-0 bottom-[calc(72px+env(safe-area-inset-bottom))] z-[115] px-3">
        <div className="grid grid-cols-2 gap-2 rounded border border-[rgba(241,89,34,0.22)] bg-white/98 p-2 backdrop-blur-sm">
          <button
            type="button"
            aria-expanded={isOpen && activePanel === "browse"}
            onClick={() => openPanel("browse")}
            className="flex min-h-12 items-center justify-between rounded bg-[#ee376a] px-3 py-2 text-right text-white active:scale-[0.98]"
          >
            <span className="flex items-center gap-2">
              <Grid2x2 className="h-4 w-4" />
              <span className="text-sm font-bold">تصفح الأقسام</span>
            </span>
            <ChevronLeft className="h-4 w-4" />
          </button>

          <button
            type="button"
            aria-expanded={isOpen && activePanel === "filter"}
            onClick={() => openPanel("filter")}
            className="relative flex min-h-12 items-center justify-between rounded bg-[#ee376a] px-3 py-2 text-right text-white active:scale-[0.98]"
          >
            <span className="flex items-center gap-2">
              <SlidersHorizontal className="h-4 w-4" />
              <span className="text-sm font-bold">
                {hasNestedFilters ? "فلترة النتائج" : "كل المنتجات"}
              </span>
            </span>
            <span className="flex items-center gap-2">
              {activeFiltersCount > 0 ? (
                <span className="rounded bg-white px-2 py-0.5 text-[11px] font-black !text-[#ee376a]">
                  {activeFiltersCount}
                </span>
              ) : (
                <span className="rounded bg-white px-2 py-0.5 text-[11px] font-black !text-[#ee376a]">
                  {visibleCount}
                </span>
              )}
              <ChevronLeft className="h-4 w-4" />
            </span>
          </button>
        </div>
      </div>
      {portalTarget
        ? createPortal(
            <AnimatePresence>
              {isOpen ? (
                <motion.div
                  className="fixed inset-0 z-[220] md:hidden"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <button
                    type="button"
                    aria-label="إغلاق الفلاتر"
                    className="absolute inset-0 bg-[rgba(22,14,26,0.52)] backdrop-blur-[10px]"
                    onClick={() => setIsOpen(false)}
                  />
                  <motion.aside
                    className="absolute inset-x-0 bottom-0 flex max-h-[78vh] flex-col overflow-hidden rounded border-t border-[rgba(241,89,34,0.2)] bg-white"
                    initial={{ y: "100%" }}
                    animate={{ y: 0 }}
                    exit={{ y: "100%" }}
                    transition={{ duration: 0.36, ease: [0.16, 1, 0.3, 1] }}
                  >
                    <div className="mx-auto mt-2 h-1.5 w-16 rounded-full bg-[rgba(109,109,109,0.26)]" />
                    <div className="flex items-center justify-between border-b border-[rgba(238,49,106,0.18)] p-4">
                      <div>
                        <p className="text-[0.74rem] font-bold uppercase tracking-[0.28em] text-[var(--color-ink-muted)]">
                          {activePanel === "browse" ? "تصفح سريع" : "فلترة المنتجات"}
                        </p>
                        <h3 className="mt-1 text-xl font-black text-[var(--color-ink)]">
                          {activePanel === "browse"
                            ? "اختَر القسم المناسب"
                            : "ابحث وفلتر بسرعة"}
                        </h3>
                        <p className="mt-1 text-xs font-bold text-[var(--color-ink-soft)]">
                          {activeFiltersCount > 0
                            ? `${activeFiltersCount} فلتر مفعل`
                            : "لا توجد فلاتر مفعلة"}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setIsOpen(false)}
                        className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-white text-[var(--color-ink)] shadow-[0_10px_24px_rgba(77,61,47,0.08)]"
                        aria-label="إغلاق الفلاتر"
                      >
                        <X className="h-5 w-5" />
                      </button>
                    </div>

                  <div className="grid grid-cols-2 gap-2 border-b border-[rgba(238,49,106,0.12)] px-4 pb-3 pt-2">
                      <button
                        type="button"
                        onClick={() => setActivePanel("browse")}
                        className={`inline-flex h-11 items-center justify-center rounded px-4 text-sm font-bold ${
                          activePanel === "browse"
                            ? "bg-[#ee376a] text-white"
                            : "border border-[var(--color-line)] bg-white text-[var(--color-ink)]"
                        }`}
                      >
                        تصفح الأقسام
                      </button>
                      <button
                        type="button"
                        onClick={() => setActivePanel("filter")}
                        className={`inline-flex h-11 items-center justify-center rounded px-4 text-sm font-bold ${
                          activePanel === "filter"
                            ? "bg-[#ee376a] text-white"
                            : "border border-[var(--color-line)] bg-white text-[var(--color-ink)]"
                        }`}
                      >
                        فلترة النتائج
                      </button>
                    </div>

                    <div className="flex-1 space-y-4 overflow-y-auto p-4 pb-28">
                      <div className="rounded-[1rem] border border-[var(--color-line)] bg-[var(--color-surface)] p-3">
                        <p className="text-xs font-semibold text-[var(--color-ink)]">
                          {selectedProductsCategoryName
                            ? `عرض أثاث ${selectedProductsCategoryName}`
                            : selectedCategoryName
                              ? `اختر قسمًا داخل ${selectedCategoryName}`
                              : "عرض كل الأثاث"}
                        </p>
                        <div className="mt-2 flex items-center gap-2 rounded-full bg-white px-3 py-1.5 text-[11px] text-[var(--color-ink-soft)]">
                          <Sofa className="h-4 w-4 text-[var(--color-accent-strong)]" />
                          <span>{allProductsCount} قطعة أثاث متاحة</span>
                        </div>
                      </div>

                      {activePanel === "browse" ? (
                        <div className="grid grid-cols-2 gap-2">
                          <button
                            type="button"
                            onClick={() => navigateTo(queryPath)}
                            className={`${baseFilterOptionClass} ${
                              !selectedCategoryId
                                ? activeFilterOptionClass
                                : inactiveFilterOptionClass
                            }`}
                          >
                            كل الأثاث ({allProductsCount})
                          </button>

                          {childCategories.map((child) => (
                            <button
                              key={child.id}
                              type="button"
                              onClick={() =>
                                navigateTo(`/products?category=${child.id}${encodedQuery}`)
                              }
                              className={`${baseFilterOptionClass} ${
                                selectedCategoryId === child.id
                                  ? activeFilterOptionClass
                                  : inactiveFilterOptionClass
                              }`}
                            >
                              {child.name}
                            </button>
                          ))}
                        </div>
                      ) : hasNestedFilters ? (
                        <div className="space-y-2">
                          <p className="text-xs font-semibold text-[var(--color-ink)]">
                            أقسام فرعية داخل {selectedCategoryName}
                          </p>
                          <div className="grid grid-cols-2 gap-2">
                            <button
                              type="button"
                              onClick={() =>
                                navigateTo(`/products?category=${selectedCategoryId}${encodedQuery}`)
                              }
                              className={`${baseNestedFilterOptionClass} ${
                                !selectedNestedChildId
                                  ? activeFilterOptionClass
                                  : inactiveFilterOptionClass
                              }`}
                            >
                              كل {selectedCategoryName}
                            </button>
                            {nestedChildCategories.map((child) => (
                              <button
                                key={child.id}
                                type="button"
                                onClick={() =>
                                  navigateTo(
                                    `/products?category=${selectedCategoryId}&child=${child.id}${encodedQuery}`,
                                  )
                                }
                                className={`${baseNestedFilterOptionClass} ${
                                  selectedNestedChildId === child.id
                                    ? activeFilterOptionClass
                                    : inactiveFilterOptionClass
                                }`}
                              >
                                {child.name}
                              </button>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <div className="rounded-[1rem] border border-dashed border-[var(--color-line)] bg-[var(--color-surface)] p-4 text-center text-sm text-[var(--color-ink-soft)]">
                          اختر قسمًا رئيسيًا أولًا حتى تظهر الأقسام الفرعية هنا.
                        </div>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-3 border-t border-[rgba(238,49,106,0.18)] bg-white p-4 pb-[calc(env(safe-area-inset-bottom)+1rem)]">
                      <button
                        type="button"
                        onClick={() => navigateTo(queryPath)}
                        className="inline-flex h-12 items-center justify-center rounded border border-[var(--color-line)] bg-white text-sm font-black text-[var(--color-ink)]"
                      >
                        مسح الفلاتر
                      </button>
                      <button
                        type="button"
                        onClick={() => setIsOpen(false)}
                        className="inline-flex h-12 items-center justify-center rounded bg-[#ee376a] px-6 text-sm font-semibold text-white"
                      >
                        عرض النتائج
                      </button>
                    </div>
                  </motion.aside>
                </motion.div>
              ) : null}
            </AnimatePresence>,
            portalTarget,
          )
        : null}
    </div>
  );
}
